/**
 * Page-level UDA scope for Employee Management: **Dashboard**, **Employee List**,
 * **Setup** and **Reports**. One selection, shared by all four.
 *
 * Background: everywhere else in the app a State/Regional ("system admin") user
 * switches UDA from the GLOBAL HEADER selector, which re-issues the JWT with a new
 * tenant. Setup masters and Reports deliberately do NOT follow the header — a system
 * admin needs to hop between UDAs while sitting on a master screen or a report,
 * without re-authenticating the whole session and losing the page they are on.
 *
 * So these screens carry their own UDA dropdown, and the header selector is disabled
 * while they are open (see Header.tsx / isUdaScopedRoute below). The choice made here
 * is remembered across the setup screens and the reports screens, so moving between
 * them keeps the same UDA.
 *
 * Non-state users have exactly one UDA and see it as a read-only field.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Autocomplete, InputAdornment, Popover, TextField } from '@mui/material';
import { useAuth } from '@/context/AuthContext';
import { useUdaOptions } from '../../hooks/useUdaOptions';
import { Skeleton } from '@/components/base/Skeleton';

/**
 * Sentinel tenant code meaning "no single UDA picked" — the rollup across every UDA
 * (plus the state's own rows). It is the value every screen here sends downstream, and
 * matches ALL_UDAS_TENANT_ID on the server.
 */
export const STATE_LEVEL_TENANT = 'default';

/** Label for the all-UDAs rollup option. */
export const ALL_UDAS_LABEL = 'All UDAs';

/**
 * Sentinel for "State/Regional Level ONLY" — the state's own employees and nothing else.
 *
 * It cannot be `'default'`: that code is simultaneously the state tenant's own code AND
 * the all-UDAs rollup sentinel, so there is no way to say "state rows only" with it.
 * The server translates `'state'` back to an exact `tenantid = 'default'` match
 * (schemaUtil.STATE_ONLY_TENANT_ID).
 *
 * ONLY the UDA Employees Dashboard understands it. {@link useEmpUdaScope} deliberately
 * reports `tenantCode` as `'default'` for this pick, so the Setup masters, the Reports
 * screens and the employee list keep behaving exactly as before; the dashboard reads the
 * distinction off `isStateOnly` instead.
 */
export const STATE_ONLY_TENANT = 'state';

/** Label used for the state-only option in every dropdown here. */
export const STATE_LEVEL_LABEL = 'State/Regional Level';

/**
 * What the un-narrowed scope is CALLED when a screen is reporting the scope currently
 * in force, rather than offering it as a choice. "All UDAs" describes the rows; this
 * describes who is looking, which is what the dashboard's toolbar chip shows.
 */
export const SYSTEM_ADMIN_LABEL = 'System admin';

interface EmpUdaScopeState {
  /**
   * Operational tenant code ('10001'-'10021'), 'default' for the all-UDAs rollup, or
   * 'state' for state-level-only. The last is a dashboard-only value — see
   * {@link STATE_ONLY_TENANT}.
   */
  tenantCode: string;
  /** ds_uda_lst_t.uda_id for the picked UDA; null at state level. */
  udaId: number | null;
  /** False until the user (or the one-time seed below) has actually chosen. */
  touched: boolean;
  setScope: (tenantCode: string, udaId: number | null) => void;
  /** One-time seed from the session's tenant; a no-op once the user has chosen. */
  seed: (tenantCode: string, udaId: number | null) => void;
}

/**
 * Persisted store so the pick survives navigation between Setup <-> Reports and a
 * page reload. It is intentionally SEPARATE from the auth tenant — nothing here
 * re-issues the JWT.
 *
 * ONE value is deliberately NOT persisted: {@link STATE_ONLY_TENANT}. State level holds a
 * single employee, so restoring it would open the dashboard on a near-empty page — it is a
 * narrowing a user asks for in the moment, not a scope to be remembered. It is written back
 * as the all-UDAs sentinel, so a reload opens on every employee. A specific UDA still
 * persists: that pick is what carries a system admin across Setup and Reports.
 */
export const useEmpUdaScopeStore = create<EmpUdaScopeState>()(
  persist(
    (set) => ({
      tenantCode: STATE_LEVEL_TENANT,
      udaId: null,
      touched: false,
      setScope: (tenantCode, udaId) => set({ tenantCode, udaId, touched: true }),
      seed: (tenantCode, udaId) =>
        set((prev) => (prev.touched ? prev : { ...prev, tenantCode, udaId, touched: true })),
    }),
    {
      name: 'emp-uda-scope',
      partialize: (s) => ({
        tenantCode: s.tenantCode === STATE_ONLY_TENANT ? STATE_LEVEL_TENANT : s.tenantCode,
        udaId: s.udaId,
        touched: s.touched,
      }),
    }
  )
);

export interface EmpUdaScope {
  /** True when the logged-in user is a State/Regional (system admin) user. */
  isStateUser: boolean;
  /**
   * Tenant code to send as `tenantId` on requests from these screens. Never 'state':
   * the state-only pick reports 'default' here so screens that do not know about it
   * keep their existing all-UDAs behaviour. Use {@link isStateOnly} to tell them apart.
   */
  tenantCode: string;
  /**
   * The raw pick as stored, INCLUDING the 'state' sentinel. Only the dropdowns (to
   * highlight the active row) and the dashboard should need it.
   */
  rawCode: string;
  /** True when the user picked "State/Regional Level" (state's own employees only). */
  isStateOnly: boolean;
  /** uda_id to send alongside it; null at state level / when unknown. */
  udaId: number | null;
  /** True when a state user has not narrowed to one UDA — writes must stay disabled. */
  noUdaSelected: boolean;
  /** UDA options for the dropdown (state users only). */
  ulbs: any[];
  loadingUlbs: boolean;
  setScope: (tenantCode: string, udaId: number | null) => void;
}

/**
 * The UDA a Setup/Reports screen should operate on.
 *
 * State users: whatever they picked in the page dropdown (NOT authUser.tenantId).
 * Everyone else: their own tenant, fixed.
 */
export function useEmpUdaScope(): EmpUdaScope {
  const { user: authUser } = useAuth();
  const isStateUser = (authUser as any)?.isStateRegionalUser || false;
  const { ulbs, loadingUlbs } = useUdaOptions();
  const tenantCode = useEmpUdaScopeStore((s) => s.tenantCode);
  const storedUdaId = useEmpUdaScopeStore((s) => s.udaId);
  const touched = useEmpUdaScopeStore((s) => s.touched);
  const setScope = useEmpUdaScopeStore((s) => s.setScope);
  const seed = useEmpUdaScopeStore((s) => s.seed);

  // First visit: start on whatever UDA the session is already in, so a system admin who
  // picked a UDA in the header before opening Setup/Reports does not land on a blank
  // state-level view. Runs once — after that the page dropdown is the only input.
  const authTenant = (authUser as any)?.tenantId;
  useEffect(() => {
    if (touched) return;
    if (!authTenant || authTenant === STATE_LEVEL_TENANT) return;
    const hit = ulbs.find((u: any) => String(u.code) === String(authTenant));
    seed(String(authTenant), (hit?.uda_id ?? (authUser as any)?.udaId ?? null) as number | null);
  }, [touched, authTenant, ulbs, seed, authUser]);

  // Re-resolve uda_id from the options list once it loads: the persisted value can be
  // stale (or absent) if the pick was made before the UDA master finished loading.
  const udaId = useMemo(() => {
    if (!isStateUser) return ((authUser as any)?.udaId ?? null) as number | null;
    if (!tenantCode || tenantCode === STATE_LEVEL_TENANT || tenantCode === STATE_ONLY_TENANT) return null;
    const hit = ulbs.find((u: any) => String(u.code) === String(tenantCode));
    return (hit?.uda_id ?? storedUdaId ?? null) as number | null;
  }, [isStateUser, tenantCode, storedUdaId, ulbs, authUser]);

  // Neither sentinel is a single UDA, so both keep writes disabled on the Setup screens.
  const isStateOnly = isStateUser && tenantCode === STATE_ONLY_TENANT;
  const noUdaSelected =
    isStateUser && (!tenantCode || tenantCode === STATE_LEVEL_TENANT || tenantCode === STATE_ONLY_TENANT);

  return {
    isStateUser,
    // 'state' is downgraded to 'default' here on purpose: every screen except the
    // dashboard should keep seeing the all-UDAs rollup it saw before this option existed.
    tenantCode: isStateUser
      ? (isStateOnly ? STATE_LEVEL_TENANT : tenantCode || STATE_LEVEL_TENANT)
      : (authUser as any)?.tenantId || STATE_LEVEL_TENANT,
    rawCode: isStateUser ? (tenantCode || STATE_LEVEL_TENANT) : ((authUser as any)?.tenantId || STATE_LEVEL_TENANT),
    isStateOnly,
    udaId,
    noUdaSelected,
    ulbs,
    loadingUlbs,
    setScope,
  };
}

/**
 * Merge the scope onto a request params object. `tenantId` stays the operational key;
 * `udaId` rides along additively (omitted when null, matching withUdaId in allUdasView).
 */
export function withScope<T extends Record<string, any>>(params: T, scope: EmpUdaScope): Record<string, any> {
  const out: Record<string, any> = { ...params, tenantId: scope.tenantCode || STATE_LEVEL_TENANT };
  if (scope.udaId !== null && scope.udaId !== undefined) out.udaId = scope.udaId;
  return out;
}

interface UdaScopeSelectProps {
  scope: EmpUdaScope;
  /** Field label. Defaults to "UDA". */
  label?: string;
  className?: string;
}

/**
 * The dropdown itself. Renders the UDA picker for state users and a disabled
 * read-only field (their own UDA) for everyone else, so the screen layout is
 * identical for both.
 */
export function UdaScopeSelect({ scope, label = 'UDA', className }: UdaScopeSelectProps) {
  const { user: authUser } = useAuth();
  const { isStateUser, rawCode, ulbs, setScope } = scope;

  // Two non-UDA choices, both above the UDA list: the rollup across every UDA, and the
  // state's own employees on their own.
  const allUdasOption = useMemo(
    () => ({ code: STATE_LEVEL_TENANT, name: ALL_UDAS_LABEL, uda_id: null }),
    []
  );
  const stateOption = useMemo(
    () => ({ code: STATE_ONLY_TENANT, name: STATE_LEVEL_LABEL, uda_id: null }),
    []
  );
  const options = useMemo(
    () => [allUdasOption, stateOption, ...ulbs],
    [allUdasOption, stateOption, ulbs]
  );

  const value = useMemo(() => {
    if (rawCode === STATE_ONLY_TENANT) return stateOption;
    if (!rawCode || rawCode === STATE_LEVEL_TENANT) return allUdasOption;
    return ulbs.find((u: any) => String(u.code) === String(rawCode)) || allUdasOption;
  }, [rawCode, ulbs, stateOption, allUdasOption]);

  const handleChange = useCallback(
    (_: unknown, next: any) => {
      if (!next || next.code === STATE_LEVEL_TENANT) {
        setScope(STATE_LEVEL_TENANT, null);
        return;
      }
      if (next.code === STATE_ONLY_TENANT) {
        setScope(STATE_ONLY_TENANT, null);
        return;
      }
      setScope(String(next.code), (next.uda_id ?? null) as number | null);
    },
    [setScope]
  );

  return (
    <div className={className ?? 'min-w-[220px] max-w-[280px]'}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {isStateUser ? (
        <Autocomplete
          size="small"
          options={options}
          getOptionLabel={(o: any) => o?.name || o?.code || ''}
          isOptionEqualToValue={(o: any, v: any) => String(o?.code) === String(v?.code ?? v ?? '')}
          value={value}
          onChange={handleChange}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select UDA..." variant="outlined" />
          )}
        />
      ) : (
        <TextField
          size="small"
          fullWidth
          disabled
          value={
            (authUser as any)?.tenants?.find((t: any) => t.code === (authUser as any)?.tenantId)?.name ||
            (authUser as any)?.tenantId ||
            ''
          }
        />
      )}
    </div>
  );
}

/** Banner shown while a state user has not narrowed to a single UDA. */
export function SelectUdaBanner({ action = 'add, edit, or delete records' }: { action?: string }) {
  return (
    <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
      <i className="ri-information-line text-base"></i>
      <span>Please select a specific UDA to {action}.</span>
    </div>
  );
}

/**
 * What the UDA scope currently in force is CALLED.
 *
 * Extracted so a screen that only REPORTS the scope (the chip below, beside a page
 * title) names it exactly the way the control that sets it does — the two were the same
 * expression written twice, which is how they drift apart.
 *
 * `isAllUdas` is returned alongside because "every UDA" is the absence of a narrowing:
 * a control has to offer it as a choice, while a label has nothing to state.
 */
export function useEmpScopeLabel(scope: EmpUdaScope): { label: string; isAllUdas: boolean } {
  const { user: authUser } = useAuth();
  const { isStateUser, rawCode, ulbs, isStateOnly } = scope;

  // Kept separate from `noUdaSelected`, which is true for the state-only pick too and
  // therefore cannot tell the two non-UDA rows apart.
  const isAllUdas = isStateUser && (!rawCode || rawCode === STATE_LEVEL_TENANT);

  const label = useMemo(() => {
    if (!isStateUser) {
      return (
        (authUser as any)?.tenants?.find((t: any) => t.code === (authUser as any)?.tenantId)?.name ||
        (authUser as any)?.tenantId ||
        'My UDA'
      );
    }
    if (isAllUdas) return ALL_UDAS_LABEL;
    if (isStateOnly) return STATE_LEVEL_LABEL;
    return ulbs.find((u: any) => String(u.code) === String(rawCode))?.name || rawCode;
  }, [isStateUser, isAllUdas, isStateOnly, ulbs, rawCode, authUser]);

  return { label, isAllUdas };
}

/**
 * The UDA in force, named beside a page title — display only, no popover.
 *
 * The module's picker ({@link UdaScopeButton}) sits at the far right of a crowded header
 * row and reads as one more action; this states which jurisdiction the rows below belong
 * to, next to the heading that names the screen. The Asset Management dashboard carries
 * the same chip in its header, so the two modules report scope the same way.
 *
 * Renders nothing at "All UDAs": the page is not narrowed, so there is no jurisdiction
 * to name, and the rollup belongs on the picker, which can say what the alternative is.
 */
export function EmpUdaScopeChip({ scope, className }: { scope: EmpUdaScope; className?: string }) {
  const { label, isAllUdas } = useEmpScopeLabel(scope);

  if (isAllUdas) return null;
  // Until the UDA master lands the label falls back to the raw tenant code ('10003'),
  // which names nothing to the user — better no chip for that frame than a number.
  if (label === scope.rawCode) return null;

  return (
    <span
      className={
        className ??
        'inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/10 ring-1 ring-inset ring-white/20 text-white text-[11px] sm:text-xs font-semibold flex-shrink-0 max-w-[45vw] sm:max-w-[220px]'
      }
      title={`Showing ${label}`}
    >
      <i className="ri-community-line text-sm text-white/70"></i>
      <span className="truncate">{label}</span>
    </span>
  );
}

interface UdaScopeButtonProps {
  scope: EmpUdaScope;
  /** Extra classes for the trigger button. */
  className?: string;
}

/**
 * The same scope as {@link UdaScopeSelect}, as a toolbar button instead of a field.
 *
 * Built for screens whose header is already crowded (the employee list, the dashboard's
 * employee table): the button shows the UDA currently in force, and opens a narrow
 * searchable DROPDOWN LIST of every UDA the user may see — one per row, the way a select
 * reads, rather than a wide card grid. Picking one writes to the shared store, so the
 * choice lands on the dashboard, the setup masters and the reports at the same time —
 * one UDA selection for the whole module, not one per screen.
 *
 * Non-state users have a single UDA, so they get a plain static label with no popover.
 */
export function UdaScopeButton({ scope, className }: UdaScopeButtonProps) {
  const { user: authUser } = useAuth();
  const { isStateUser, rawCode, ulbs, loadingUlbs, isStateOnly, setScope } = scope;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState('');

  // The label, and the "nothing narrowed" flag the tick marks below key off — shared with
  // EmpUdaScopeChip so the control and the chip can never name the same scope differently.
  const { label: currentLabel, isAllUdas } = useEmpScopeLabel(scope);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ulbs;
    return ulbs.filter((u: any) =>
      String(u.name || '').toLowerCase().includes(q) ||
      String(u.uda_cd || '').toLowerCase().includes(q) ||
      String(u.code || '').toLowerCase().includes(q)
    );
  }, [ulbs, query]);

  const pick = useCallback(
    (code: string | null, udaId: number | null) => {
      setScope(code ?? STATE_LEVEL_TENANT, udaId);
      setAnchorEl(null);
      setQuery('');
    },
    [setScope]
  );

  // One UDA, nothing to choose between: show it, don't offer a picker.
  if (!isStateUser) {
    return (
      <span
        className={
          className ??
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 text-white text-[13px] font-medium'
        }
        title="Your UDA"
      >
        <i className="ri-building-2-line"></i>
        <span className="truncate max-w-[160px]">{currentLabel}</span>
      </span>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        aria-haspopup="dialog"
        aria-expanded={!!anchorEl}
        title="Choose the UDA this screen works on"
        className={
          className ??
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 hover:bg-white/25 text-white text-[13px] font-medium transition-colors'
        }
      >
        <i className="ri-building-2-line"></i>
        <span className="truncate max-w-[160px]">{currentLabel}</span>
        <i className={anchorEl ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
      </button>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => { setAnchorEl(null); setQuery(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 2, width: 'min(320px, calc(100vw - 32px))' } } }}
      >
        {/* A dropdown, not a grid: one UDA per row, so the list scans vertically the way
            a select does. The search box earns its place at 21 UDAs. */}
        <div className="p-2">
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search UDA..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <i className="ri-search-line text-gray-400"></i>
                  </InputAdornment>
                ),
              },
            }}
          />
        </div>

        <div className="max-h-[320px] overflow-auto pb-2">
          {/* Neither of these is one of the UDAs, so both sit above the divider: the
              rollup across every UDA, and the state's own employees on their own.
              Both are read-only for writes. */}
          <button
            type="button"
            onClick={() => pick(STATE_LEVEL_TENANT, null)}
            className={`w-full text-left px-3 py-2 flex items-center gap-2 text-[13px] transition-colors ${
              isAllUdas ? 'bg-violet-50 text-violet-800 font-semibold' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className="ri-stack-line text-gray-400"></i>
            <span className="truncate">{ALL_UDAS_LABEL}</span>
            <span className="ml-auto text-[11px] text-gray-400 flex-shrink-0">Every UDA</span>
            {isAllUdas && <i className="ri-check-line text-violet-600"></i>}
          </button>

          <button
            type="button"
            onClick={() => pick(STATE_ONLY_TENANT, null)}
            className={`w-full text-left px-3 py-2 flex items-center gap-2 text-[13px] transition-colors ${
              isStateOnly ? 'bg-violet-50 text-violet-800 font-semibold' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <i className="ri-government-line text-gray-400"></i>
            <span className="truncate">{STATE_LEVEL_LABEL}</span>
            <span className="ml-auto text-[11px] text-gray-400 flex-shrink-0">State only</span>
            {isStateOnly && <i className="ri-check-line text-violet-600"></i>}
          </button>

          <div className="my-1 border-t border-gray-100"></div>

          {loadingUlbs ? (
            <div className="px-3 py-3 space-y-2">
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">No UDA matches "{query}".</p>
          ) : (
            filtered.map((u: any) => {
              const active = String(u.code) === String(rawCode);
              return (
                <button
                  key={u.code}
                  type="button"
                  onClick={() => pick(String(u.code), (u.uda_id ?? null) as number | null)}
                  title={u.name}
                  className={`w-full text-left px-3 py-2 flex items-center gap-2 text-[13px] transition-colors ${
                    active ? 'bg-violet-50 text-violet-800 font-semibold' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="truncate">{u.name}</span>
                  <span className="ml-auto text-[11px] text-gray-400 flex-shrink-0">
                    {u.uda_cd || u.code}
                  </span>
                  {active && <i className="ri-check-line text-violet-600 flex-shrink-0"></i>}
                </button>
              );
            })
          )}
        </div>
      </Popover>
    </>
  );
}

/**
 * Routes that own their UDA scope and therefore IGNORE the global header selector.
 * Header.tsx disables its own dropdown for these paths so the two cannot disagree.
 */
export function isUdaScopedRoute(pathname: string): boolean {
  const p = (pathname || '').toLowerCase().replace(/\/+$/, '');
  const EM = '/maud-erp/employee-management';
  return (
    p.includes(`${EM}/setup`) ||
    p.includes(`${EM}/reports`) ||
    // The employee list — its UDA grid button drives the same shared scope.
    p.endsWith(`${EM}/list`) ||
    // User Management (Users List) — same page-level picker, same shared scope.
    p.endsWith(`${EM}/users-list`) ||
    // The dashboard, on both the explicit path and the module's index route.
    p.endsWith(`${EM}/dashboard`) ||
    p.endsWith(EM)
  );
}

/**
 * The UDA a Setup FORM writes its record to, and the dropdown that picks it.
 *
 * Setup masters used to refuse to write at all unless the page had been narrowed to one
 * UDA — a banner replaced the Add button. That put the choice in the wrong place: the page
 * scope is a filter over what you are LOOKING at, while a new record needs to be told where
 * it BELONGS. So the form asks instead, and the page never blocks.
 *
 * "All UDAs" is a real target here, not the absence of one: it means the DTCP head office —
 * tenantid 'default', uda_id 0 — the same place the head-office departments live.
 *
 * On EDIT the target is fixed to the row's own tenant and the control is read-only: ids are
 * per-tenant, so moving a record between UDAs is not an edit, it is a different record.
 */
export interface UdaTarget {
  /** Tenant code to send as `tenantId` on the save. '' until a state admin has chosen. */
  tenantCode: string;
  /**
   * uda_id to send alongside it. ZERO for "All UDAs" — that option means the DTCP head
   * office, which is a real place with a real marker, not the absence of a UDA. null only
   * when nothing has been chosen yet.
   */
  udaId: number | null;
  /** True once the form has a target it can write to. */
  ready: boolean;
  /** True when the acting user has no choice to make (single-UDA admin, or an edit). */
  locked: boolean;
  setTenantCode: (code: string) => void;
  /** Options for the dropdown, "All UDAs" first. */
  options: { code: string; name: string }[];
  /** Display label for the locked case. */
  label: string;
}

/**
 * Resolve the write target for a Setup form.
 *
 * @param scope     the page's UDA scope (from useEmpUdaScope)
 * @param rowTenant the tenant of the row being edited; undefined when creating
 * @param isOpen    resets the choice each time the dialog opens
 */
export function useUdaTarget(scope: EmpUdaScope, rowTenant?: string | null, isOpen = true): UdaTarget {
  const { isStateUser, ulbs, tenantCode: pageTenant, isStateOnly } = scope;
  // Seed from the page: a state admin sitting on one UDA is almost always filing there,
  // and "All UDAs"/State level seeds the head office, which is a legitimate target too.
  const seed = isStateUser ? (isStateOnly ? STATE_LEVEL_TENANT : pageTenant || '') : pageTenant;
  const [chosen, setChosen] = useState<string>(seed || '');

  useEffect(() => {
    if (isOpen) setChosen(seed || '');
  }, [isOpen, seed]);

  const options = useMemo(
    () => [
      { code: STATE_LEVEL_TENANT, name: ALL_UDAS_LABEL },
      ...ulbs.map((u: any) => ({ code: String(u.code), name: u.name || String(u.code) })),
    ],
    [ulbs]
  );

  // Editing: the row decides, and it cannot be moved. Not a state user: their one tenant.
  const fixed = rowTenant || (!isStateUser ? pageTenant : '');
  const tenantCode = fixed || chosen;

  const isHeadOffice = !!tenantCode && String(tenantCode) === STATE_LEVEL_TENANT;
  const label =
    !tenantCode || isHeadOffice
      ? ALL_UDAS_LABEL
      : options.find((o) => o.code === String(tenantCode))?.name || String(tenantCode);

  // HEAD_OFFICE_UDA_ID on the server. Kept literal here rather than fetched: it is a
  // constant of the data model, and the form must be able to state it with no round trip.
  const udaId = !tenantCode
    ? null
    : isHeadOffice
      ? 0
      : (ulbs.find((u: any) => String(u.code) === String(tenantCode))?.uda_id ?? null);

  return {
    tenantCode: String(tenantCode || ''),
    udaId,
    ready: !!tenantCode,
    locked: !!fixed,
    setTenantCode: setChosen,
    options,
    label,
  };
}

/**
 * The dropdown for {@link useUdaTarget}. Renders a read-only field when the target is
 * already decided, so the form's layout does not jump between create and edit.
 */
export function UdaTargetSelect({
  target,
  label = 'UDA',
  className = 'mb-4',
  error,
}: {
  target: UdaTarget;
  label?: string;
  className?: string;
  error?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {!target.locked && <span className="text-red-500">*</span>}
      </label>
      {target.locked ? (
        <TextField size="small" fullWidth disabled value={target.label} />
      ) : (
        <>
          <Autocomplete
            size="small"
            options={target.options}
            getOptionLabel={(o: any) => o?.name || o?.code || ''}
            isOptionEqualToValue={(o: any, v: any) => String(o?.code) === String(v?.code)}
            value={target.options.find((o) => o.code === target.tenantCode) || null}
            onChange={(_, v: any) => target.setTenantCode(v ? String(v.code) : '')}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select UDA" error={!!error} />
            )}
          />
          {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
        </>
      )}
    </div>
  );
}
